import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { apiError } from "../../shared/api/client";
import {
  platformApi,
  platformQueryKeys,
  type PlatformTenant,
  type TenantScreen,
  type TenantScreens,
} from "../../shared/api/platform";
import {
  FeatureBrowser,
  FeatureName,
  type FeatureFilter,
} from "./FeatureBrowser";
import { PlatformTenantPlanSection } from "./PlatformTenantPlanSection";
import {
  planScreenApi,
  planScreenQueryKeys,
  type ScreenItem,
} from "../../shared/api/platformBilling";

/** Hai bộ lọc thường dùng nhất khi rà một phòng khám: cái gì đang tắt, cái gì khác với gói. */
const FILTERS: FeatureFilter<TenantScreen>[] = [
  {
    key: "off",
    label: "Đang tắt",
    title: "Đang tắt",
    match: (e) => !e.state.enabled,
  },
  {
    key: "overridden",
    label: "Chỉnh riêng",
    title: "Chỉnh riêng cho phòng khám này",
    tone: "amber",
    // `!= null`, không phải `!== null`: màn hình theo gói tới đây không có khoá `override`.
    match: (e) => e.state.override != null,
  },
];

/**
 * Gói và tính năng của MỘT phòng khám (ADR-0053): gán gói, áp dụng giới hạn theo gói, và bật/tắt
 * từng tính năng cho riêng phòng khám đó, đè lên gói của họ.
 *
 * <p>Danh mục có hàng chục tính năng trong hơn mười nhóm, nên hộp thoại chia hai khung: bên trái
 * là các nhóm cùng hai bộ lọc thường dùng nhất ("Đang tắt", "Chỉnh riêng"), bên phải chỉ hiện
 * nhóm đang chọn. Ô tìm kiếm tìm trên toàn bộ danh mục.
 *
 * <p>Mỗi tính năng là MỘT công tắc: bật = phòng khám thấy trong menu. Gạt khác với gói là tạo một
 * ngoại lệ, nên ô lý do mở ngay dưới dòng đó; lý do vào nhật ký kiểm toán của chính phòng khám,
 * cùng tên người bấm. Gạt một ngoại lệ về đúng giá trị của gói là bỏ ngoại lệ — không cần lý do,
 * cũng như "Về theo gói". Mỗi lúc chỉ một dòng mở ô lý do; gạt ngược lại, bấm Huỷ hay Esc đều đóng
 * ô đó mà không lưu gì — Esc đóng ô lý do TRƯỚC, bấm lần nữa mới đóng hộp thoại.
 *
 * <p>Khác bảng tick của gói, ở đây phụ thuộc KHÔNG tự kéo theo: đổi một tính năng của một phòng
 * khám đang chạy mà âm thầm đổi thêm tính năng khác là thay đổi thứ không ai yêu cầu. Máy chủ từ
 * chối và nói rõ phải bật/tắt gì trước; câu đó được hiện nguyên văn tại dòng vừa gạt.
 */
export function PlatformTenantScreensDialog({
  tenant,
  open,
  onOpenChange,
}: {
  tenant: PlatformTenant;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  /** Dòng đang mở ô lý do, và giá trị nó sắp được gạt sang. */
  const [pending, setPending] = useState<{
    code: string;
    enabled: boolean;
  } | null>(null);

  const catalogQ = useQuery({
    queryKey: planScreenQueryKeys.catalog(),
    queryFn: () => planScreenApi.catalog(),
    staleTime: Infinity,
    enabled: open,
  });
  const screensQ = useQuery({
    queryKey: platformQueryKeys.tenantScreens(tenant.id),
    queryFn: () => platformApi.tenantScreens(tenant.id),
    enabled: open,
  });

  const change = useMutation({
    mutationFn: (
      c:
        | { code: string; enabled: boolean; reason: string }
        | { code: string; enabled: null },
    ): Promise<TenantScreens> =>
      c.enabled === null
        ? platformApi.clearTenantScreenOverride(tenant.id, c.code)
        : platformApi.overrideTenantScreen(
            tenant.id,
            c.code,
            c.enabled,
            c.reason,
          ),
    onSuccess: (data) => {
      qc.setQueryData(platformQueryKeys.tenantScreens(tenant.id), data);
      setPending(null);
    },
  });

  const ask = (next: { code: string; enabled: boolean } | null) => {
    change.reset();
    setPending(next);
  };

  const toggle = (state: TenantScreen, enabled: boolean) => {
    if (enabled === state.enabled) {
      // Gạt về đúng giá trị đang có: chỉ xảy ra khi dòng này đang chờ lý do — tức là đổi ý.
      ask(null);
      return;
    }
    // `!= null`, không phải `!== null`: màn hình theo gói tới đây không có khoá `override`.
    if (state.override != null && enabled === state.inPlan) {
      ask(null);
      change.mutate({ code: state.code, enabled: null });
    } else {
      ask({ code: state.code, enabled });
    }
  };

  // Lời từ chối hiện ngay tại dòng vừa gạt: một dòng lỗi ở đầu hộp thoại nằm ngoài tầm mắt và
  // cú gạt trông như không có tác dụng gì.
  const refusal = change.isError
    ? { code: change.variables.code, detail: apiError(change.error).detail }
    : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) ask(null);
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="flex h-[85vh] max-w-5xl flex-col gap-4"
        onEscapeKeyDown={(e) => {
          if (pending) {
            e.preventDefault();
            ask(null);
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Gói &amp; tính năng</DialogTitle>
          <DialogDescription>{tenant.name}</DialogDescription>
        </DialogHeader>

        {(catalogQ.isLoading || screensQ.isLoading) && (
          <Skeleton className="h-40 w-full" />
        )}
        {(catalogQ.isError || screensQ.isError) && (
          <p className="text-sm text-destructive">
            {apiError(catalogQ.error ?? screensQ.error).detail}
          </p>
        )}

        {catalogQ.data && screensQ.data && (
          <>
            <PlatformTenantPlanSection tenant={tenant} data={screensQ.data} />
            <FeatureBrowser
              catalog={catalogQ.data}
              stateOf={(code) =>
                screensQ.data.screens.find((s) => s.code === code)
              }
              isOn={(s) => s.enabled}
              filters={FILTERS}
              hint="Bật là phòng khám thấy trong menu. Bật/tắt khác với gói cần ghi lý do."
              onNavigate={() => ask(null)}
              renderRow={({ screen, state }, groupLabel) => (
                <ScreenRow
                  key={screen.code}
                  screen={screen}
                  state={state}
                  groupLabel={groupLabel}
                  busy={change.isPending}
                  pendingTarget={
                    pending?.code === screen.code ? pending.enabled : undefined
                  }
                  refusal={
                    refusal?.code === screen.code ? refusal.detail : undefined
                  }
                  onToggle={(enabled) => toggle(state, enabled)}
                  onConfirm={(reason) =>
                    pending && change.mutate({ ...pending, reason })
                  }
                  onCancel={() => ask(null)}
                  onClear={() =>
                    change.mutate({ code: screen.code, enabled: null })
                  }
                />
              )}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ScreenRow({
  screen,
  state,
  groupLabel,
  busy,
  pendingTarget,
  refusal,
  onToggle,
  onConfirm,
  onCancel,
  onClear,
}: {
  screen: ScreenItem;
  state: TenantScreen;
  /** Tên nhóm, khi danh sách đang trộn nhiều nhóm. */
  groupLabel?: string;
  busy: boolean;
  /** Giá trị dòng này đang chờ lý do để gạt sang; `undefined` = không chờ. */
  pendingTarget?: boolean;
  /** Vì sao lần chỉnh gần nhất trên dòng này bị từ chối, nếu có. */
  refusal?: string;
  onToggle: (enabled: boolean) => void;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  /** Bỏ chỉnh riêng, về theo gói. */
  onClear: () => void;
}) {
  const [reason, setReason] = useState("");
  const overridden = state.override != null;
  const asking = pendingTarget !== undefined;

  return (
    <li className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <FeatureName label={screen.label} groupLabel={groupLabel} />
          {screen.locked && (
            <p
              className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"
              title="Tính năng nền của hệ thống: có sẵn trong mọi gói, không chỉnh riêng được."
            >
              <Lock className="h-3 w-3" /> Tính năng nền · luôn bật
            </p>
          )}
          {overridden && (
            <p className="mt-0.5 flex min-w-0 items-center gap-2 text-xs">
              <span
                className="truncate text-amber-600"
                title={state.reason ?? undefined}
              >
                Chỉnh riêng{state.reason ? `: ${state.reason}` : ""}
              </span>
              <button
                type="button"
                className="shrink-0 font-medium text-foreground underline underline-offset-2 disabled:opacity-50"
                disabled={busy}
                onClick={onClear}
              >
                Về theo gói
              </button>
            </p>
          )}
        </div>
        {/* Đang chờ lý do thì công tắc vẫn gạt được: gạt ngược lại là đổi ý. */}
        <Switch
          aria-label={screen.label}
          checked={asking ? pendingTarget : state.enabled}
          disabled={screen.locked || busy}
          onCheckedChange={(enabled) => {
            setReason("");
            onToggle(enabled);
          }}
        />
      </div>

      {asking && (
        <form
          className="mt-3 flex flex-wrap items-end gap-2 rounded-md bg-muted/50 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim()) onConfirm(reason.trim());
          }}
        >
          <label className="grid min-w-[16rem] flex-1 gap-1.5 text-xs font-medium">
            {pendingTarget
              ? "Lý do bật riêng cho phòng khám này"
              : "Lý do tắt riêng cho phòng khám này"}
            <Input
              autoFocus
              className="h-8"
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ghi vào nhật ký của phòng khám"
            />
          </label>
          <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
            Huỷ
          </Button>
          <Button type="submit" size="sm" disabled={busy || !reason.trim()}>
            {busy ? "Đang lưu…" : "Lưu"}
          </Button>
        </form>
      )}

      {refusal && (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {refusal}
        </p>
      )}
    </li>
  );
}
